import { useDictionaryContext } from 'application/contexts/DictionaryContext';
import { useSiteContext } from 'application/contexts/SiteContext';
import { LabelKey } from 'application/repositories/dictionaries';
import { getPhoneWaitTime } from 'application/repositories/phoneRepository';
import { Paragraph } from 'designsystem/components/1-atoms/Paragraph';
import { ContactBox } from 'designsystem/components/2-molecules/ContactBox';
import { Abbreviation } from 'helpers/Abbreviation';
import { fireAdobeEvent } from 'helpers/adobeEvent';
import { useCallback, useEffect, useState } from 'react';
import { LinkFeature } from '../Link';

export const ContactBoxFeature: React.FC<Content.ContactBox> = ({ content }) => {
	const { heading, contact } = content?.properties ?? {};
	const { phoneNumber, disableWaitTime, active, ...daysAndOpeningHours } = contact?.[0]?.properties ?? {};
	const dictionary = useDictionaryContext();
	const siteContext = useSiteContext();
	const { settings: siteSettings } = siteContext ?? {};
	const disableWaitTimeGlobal = !siteSettings?.contactBoxWaitTime?.active;
	const [phoneActive, setPhoneActive] = useState(false);
	const [currentOpeningHours, setCurrentOpeningHours] = useState(null);
	const [waitTime, setWaitTime] = useState(null);
	const [openingState, setOpeningState] = useState<'OPEN' | 'WILL_OPEN' | 'CLOSED'>('CLOSED');

	const getState = (todaysOpeningTime, todaysClosingTime): 'OPEN' | 'WILL_OPEN' | 'CLOSED' => {
		if (!todaysOpeningTime) return 'CLOSED';
		const todaysDate = new Date();
		if (isClosed(todaysDate)) return 'CLOSED';

		const currentHour = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(todaysDate);
		if (currentHour > todaysOpeningTime && currentHour < todaysClosingTime) return 'OPEN';
		if (currentHour < todaysOpeningTime) return 'WILL_OPEN';
		if (currentHour > todaysClosingTime) return 'CLOSED';
	};

	const getNextDayWithOpeningHours = useCallback((daysAndHours: object) => {
		let nextOpeningDaysOpeningHours = null;
		let nextOpeningDay: LabelKey;
		let daysTested = 0;
		const tempToday = new Date();
		while (!nextOpeningDaysOpeningHours && daysTested < 6) {
			const testDate = tempToday.setDate(tempToday.getDate() + 1);
			const closed = isClosed(new Date(testDate));
			if (!closed) {
				const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(testDate);
				nextOpeningDay = weekday as LabelKey;
				nextOpeningDaysOpeningHours =
					daysAndHours[new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(testDate).toLocaleLowerCase()];
			}
			daysTested++;
		}
		const openingTime = nextOpeningDaysOpeningHours?.substring(0, nextOpeningDaysOpeningHours.indexOf('-'));
		return { nextOpeningDay, openingTime };
	}, []);
	useEffect(() => {
		if (!daysAndOpeningHours) return;
		const hasValue = Object.values(daysAndOpeningHours).some((value) => { return value !== null });
		if (!hasValue) return;
		const abbreviatedClockString = Abbreviation(dictionary, 'Clock');
		const todaysDate = new Date();
		const closed = isClosed(todaysDate);
		const todaysDay = new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(todaysDate).toLocaleLowerCase().toLocaleLowerCase();
		const todaysOpeningHours = !closed ? daysAndOpeningHours[todaysDay] : null;
		const todaysOpeningTime = todaysOpeningHours?.substring(0, todaysOpeningHours.indexOf('-'));
		const todaysClosingTime = todaysOpeningHours?.substring(todaysOpeningHours.indexOf('-') + 1);

		const state = getState(todaysOpeningHours, todaysClosingTime);
		setOpeningState(state);

		if (state === 'OPEN') {
			setPhoneActive(true);
			setCurrentOpeningHours(`${abbreviatedClockString}  ${todaysClosingTime}`);
			return;
		}
		if (state === 'WILL_OPEN')
			return setCurrentOpeningHours(`${dictionary.getServiceInfo('WeOpen$Clock$Time', [abbreviatedClockString, todaysOpeningTime])}`);
		if (state === 'CLOSED') {
			const tomorrow = todaysDate.setDate(todaysDate.getDate() + 1);
			const tomorrowsOpeningHours = daysAndOpeningHours[new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(tomorrow)];
			if (tomorrowsOpeningHours) {
				return setCurrentOpeningHours(
					`${dictionary.getServiceInfo('WeOpen$Day$Clock$Time', [
						dictionary.getLabel('Tomorrow').toLowerCase(),
						abbreviatedClockString,
						tomorrowsOpeningHours.substring(0, tomorrowsOpeningHours.indexOf('-')),
					])}`,
				);
			}

			const nextOpeningHours = getNextDayWithOpeningHours(daysAndOpeningHours);
			if (nextOpeningHours.openingTime !== '') {
				return setCurrentOpeningHours(
					`${dictionary.getServiceInfo('WeOpen$Day$Clock$Time', [
						dictionary.getLabel(nextOpeningHours.nextOpeningDay),
						abbreviatedClockString,
						nextOpeningHours.openingTime,
					])}`,
				);
			}
			setCurrentOpeningHours(null);
		}
	}, [dictionary, getNextDayWithOpeningHours, daysAndOpeningHours, phoneNumber]);

	useEffect(() => {
		if (disableWaitTimeGlobal || disableWaitTime) return;
		if (openingState !== 'OPEN') {
			setWaitTime(null);
			return;
		}

		const getWaitTime = async () => {
			const { waitTime } = (await getPhoneWaitTime(phoneNumber.replaceAll(' ', ''))) ?? {};
			if (!waitTime) return;
			setWaitTime(waitTime);
		};
		getWaitTime();
	}, [phoneNumber, openingState, disableWaitTime, disableWaitTimeGlobal]);

	const handleTracking = () => {
		fireAdobeEvent('phone_clicked', { name: 'phone number click' });
	};

	return (
		active && (
			<ContactBox
				heading={heading}
				contactText={`${dictionary.getAction('ContactUs')} ${
					openingState === 'OPEN' ? '<br>' + dictionary.getLabel('Before').toLowerCase() + currentOpeningHours : ''
				}`}
				phoneNumberLink={
					phoneActive ? (
						<LinkFeature url={`tel:${phoneNumber}`} title={phoneNumber} style="arrow" callback={handleTracking} />
					) : (
						<Paragraph text={phoneNumber} />
					)
				}
				currentWaitingTime={
					waitTime
						? `${dictionary.getServiceInfo('ExpectedWaitingTime')}: 
					<br>
					${Abbreviation(dictionary, 'Approximately')} 
					${waitTime} 
					${dictionary.getLabel('Minutes').toLowerCase()}`
						: ''
				}
				openingHours={openingState !== 'OPEN' && currentOpeningHours}
			/>
		)
	);
};

// There is no fixed ruleset, so for now these dates are hard coded
// This should probably be data on the shared instance, when we get that
const isClosed = (date: Date): boolean => {
	switch (date.toLocaleDateString('da-DK')) {
		case '21.11.2023':
		case '25.12.2023':
		case '26.12.2023':
		case '27.12.2023':
		case '1.1.2024':
		case '28.3.2024':
		case '29.3.2024':
		case '1.4.2024':
		case '9.5.2024':
		case '10.5.2024':
		case '20.5.2024':
		case '24.12.2024':
		case '25.12.2024':
		case '26.12.2024':
		case '27.12.2024':
		case '31.12.2024':
		case '1.1.2025':
			return true;
		default:
			return false;
	}
};
